import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MoovyCard, MoovyFilterBar, MoovyProgress, MoovyTable, } from 'moovy-ui-core';
import { useApplications } from '../../apis/applications/useApplications.js';
import { useLocations } from '../../apis/locations/useLocations.js';
import { getLocalized } from '../../utils/getLocalized.js';
import ApplicationStatusChip from './components/applicationStatusChip.js';
const ALL_LOCATIONS = '';
const formatDate = (isoDate, locale) => {
    const dt = DateTime.fromISO(isoDate);
    if (!dt.isValid)
        return isoDate;
    return locale.split('-')[0] === 'fi'
        ? dt.toFormat('dd.MM.yyyy')
        : dt.toFormat('MM/dd/yyyy');
};
const ApplicationsPage = () => {
    var _a, _b, _c;
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const locationRefParam = (_a = searchParams.get('location_ref')) !== null && _a !== void 0 ? _a : ALL_LOCATIONS;
    const locationRef = locationRefParam || undefined;
    const { data, isLoading, error } = useApplications({
        status: 'APPLIED',
        locationRef,
    });
    const { data: locationsData } = useLocations();
    const updateSearchParams = (updates) => {
        const next = new URLSearchParams(searchParams);
        for (const [key, value] of Object.entries(updates)) {
            if (value === undefined || value === '') {
                next.delete(key);
            }
            else {
                next.set(key, value);
            }
        }
        setSearchParams(next);
    };
    const handleFilterChange = (values) => {
        var _a;
        const value = (_a = values.location) !== null && _a !== void 0 ? _a : ALL_LOCATIONS;
        updateSearchParams({
            location_ref: value === ALL_LOCATIONS ? undefined : value,
        });
    };
    const [searchValue, setSearchValue] = useState('');
    const allApplications = (_b = data === null || data === void 0 ? void 0 : data.agreements) !== null && _b !== void 0 ? _b : [];
    const applications = searchValue
        ? allApplications.filter((app) => {
            const term = searchValue.toLowerCase();
            return (app.companyName.toLowerCase().includes(term) ||
                app.businessId.toLowerCase().includes(term) ||
                getLocalized(app.product.name, i18n.language)
                    .toLowerCase()
                    .includes(term) ||
                app.locationName.toLowerCase().includes(term));
        })
        : allApplications;
    const columns = [
        {
            id: 'companyName',
            label: t('applications.table.companyName'),
            field: 'companyName',
        },
        {
            id: 'businessId',
            label: t('applications.table.businessId'),
            field: 'businessId',
        },
        {
            id: 'product',
            label: t('applications.table.product'),
            renderCell: (row) => getLocalized(row.product.name, i18n.language),
        },
        {
            id: 'locationName',
            label: t('applications.table.location'),
            field: 'locationName',
        },
        {
            id: 'submittedAt',
            label: t('applications.table.submittedAt'),
            renderCell: (row) => formatDate(row.submittedAt, i18n.language),
        },
        {
            id: 'status',
            label: t('applications.table.status'),
            renderCell: (row) => _jsx(ApplicationStatusChip, { status: row.status }),
        },
    ];
    const locationOptions = ((_c = locationsData === null || locationsData === void 0 ? void 0 : locationsData.locations) !== null && _c !== void 0 ? _c : []).map((loc) => ({
        value: loc.ref,
        label: loc.name,
    }));
    const renderContent = () => {
        if (isLoading) {
            return _jsx(MoovyProgress, {});
        }
        if (error) {
            return (_jsx(Typography, { color: "error", sx: { py: 2 }, children: t('common.error') }));
        }
        if (!Array.isArray(applications) || applications.length === 0) {
            return (_jsx(Typography, { color: "text.secondary", sx: { py: 2 }, children: t('applications.emptyState') }));
        }
        if (isMobile) {
            return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: applications.map((app) => (_jsx(MoovyCard, { cardTitle: app.companyName, cardSecondaryTitle: app.businessId, cardContentList: [
                        _jsxs(Typography, { component: "span", variant: "body2", color: "text.primary", children: [t('applications.table.product'), ":", ' ', getLocalized(app.product.name, i18n.language)] }, "product"),
                        _jsxs(Typography, { component: "span", variant: "body2", color: "text.primary", children: [t('applications.table.location'), ": ", app.locationName] }, "location"),
                        _jsxs(Typography, { component: "span", variant: "body2", color: "text.primary", children: [t('applications.table.submittedAt'), ":", ' ', formatDate(app.submittedAt, i18n.language)] }, "date"),
                        _jsx(Typography, { component: "span", variant: "body2", color: "text.primary", sx: { fontWeight: 600 }, children: t(`applications.status.${app.status}`) }, "status"),
                    ], onCardClicked: () => navigate(`/applications/${app.ref}`) }, app.ref))) }));
        }
        return (_jsx(MoovyTable, { columns: columns, data: applications, getRowKey: (row) => row.ref, loading: isLoading, error: error ? t('common.error') : null, emptyMessage: t('applications.emptyState'), hover: true, onRowClick: (row) => navigate(`/applications/${row.ref}`) }));
    };
    return (_jsxs(Box, { children: [_jsx(Typography, { variant: "h4", gutterBottom: true, sx: { fontWeight: 700 }, children: t('applications.title') }), _jsxs(Paper, { elevation: 2, sx: { p: 3 }, children: [_jsx(MoovyFilterBar, { searchPlaceholder: t('applications.search.placeholder'), searchValue: searchValue, onSearchChange: setSearchValue, filters: [
                            {
                                field: 'location',
                                label: t('applications.filter.location'),
                                placeholder: t('applications.filter.allLocations'),
                                type: 'select',
                                options: locationOptions,
                            },
                        ], filterValues: { location: locationRefParam }, onFilterChange: handleFilterChange }), _jsx(Box, { sx: { mt: 2 } }), renderContent()] })] }));
};
export default ApplicationsPage;
